import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Cookie, Save, Loader2, Settings, Info, FileText, Code, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCookieConsent = () => {
  const { getAuthHeader, token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [settings, setSettings] = useState({
    enabled: true,
    gtmId: 'GTM-N2B4HCC',
    privacyPolicySlug: 'polityka-prywatnosci',
    termsOfServiceSlug: ''
  });

  useEffect(() => {
    // Wait for token to be available before fetching
    if (token) {
      fetchData();
    } else {
      // If no token available, stop loading (will be handled by AdminLayout redirect)
      setLoading(false);
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [settingsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/admin/cookie-consent/settings`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/documents`, { headers: getAuthHeader() })
      ]);
      
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Nie udało się pobrać ustawień');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/cookie-consent/settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('Ustawienia zapisane pomyślnie');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Cookie className="w-7 h-7 text-primary" />
            Ustawienia GDPR / Cookie Consent
          </h1>
          <p className="text-gray-400 mt-1">
            Konfiguracja popupu zgody na pliki cookie (Consent Mode v2)
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Zapisz ustawienia
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-gray-300 text-sm">
            <p>Popup Cookie Consent jest zgodny z <strong>Google Consent Mode v2</strong> i wymogami GDPR.</p>
            <p className="text-gray-500 text-xs mt-1">
              Teksty popupu można edytować w zakładce <strong>Tłumaczenia → Cookie Consent (GDPR)</strong>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Główne ustawienia
          </CardTitle>
          <CardDescription>
            Włączanie/wyłączanie popupu i konfiguracja GTM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-4 bg-black/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${settings.enabled ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                <Cookie className={`w-5 h-5 ${settings.enabled ? 'text-green-500' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-white font-medium">Popup Cookie Consent</p>
                <p className="text-sm text-gray-400">
                  {settings.enabled 
                    ? 'Popup wyświetla się nowym użytkownikom' 
                    : 'Popup jest wyłączony'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          {/* GTM ID */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-gray-400" />
              <Label className="text-white">Google Tag Manager ID</Label>
            </div>
            <Input
              type="text"
              placeholder="GTM-XXXXXXX"
              value={settings.gtmId}
              onChange={(e) => setSettings({ ...settings, gtmId: e.target.value })}
              className="w-64 bg-black/30 border-white/10"
            />
            <p className="text-sm text-gray-500">
              ID kontenera GTM do śledzenia zgód. Zostaw puste jeśli nie używasz GTM.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documents Selection */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Dokumenty prawne
          </CardTitle>
          <CardDescription>
            Wybierz dokumenty wyświetlane w popupie cookie consent
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Privacy Policy */}
          <div className="space-y-3">
            <Label className="text-white font-medium">Polityka prywatności *</Label>
            <Select
              value={settings.privacyPolicySlug || ''}
              onValueChange={(value) => setSettings({ ...settings, privacyPolicySlug: value })}
            >
              <SelectTrigger className="w-full p-3 h-auto rounded-lg bg-black/30 border border-white/10 text-white">
                <SelectValue placeholder="-- Wybierz dokument --" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10">
                <SelectItem value="__none__" className="text-gray-400">-- Wybierz dokument --</SelectItem>
                {documents.map((doc) => {
                  const docSlug = typeof doc.slug === 'object' ? doc.slug?.pl : doc.slug;
                  return (
                    <SelectItem 
                      key={docSlug || doc.document_id} 
                      value={docSlug || ''}
                      className="text-white hover:bg-white/10"
                    >
                      {doc.title?.pl || doc.title || docSlug || 'Bez tytułu'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {settings.privacyPolicySlug && (
              <a 
                href={`/pl/dokument/${settings.privacyPolicySlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Podgląd dokumentu
              </a>
            )}
          </div>

          {/* Terms of Service */}
          <div className="space-y-3">
            <Label className="text-white font-medium">Regulamin (opcjonalny)</Label>
            <Select
              value={settings.termsOfServiceSlug || '__none__'}
              onValueChange={(value) => setSettings({ ...settings, termsOfServiceSlug: value === '__none__' ? '' : value })}
            >
              <SelectTrigger className="w-full p-3 h-auto rounded-lg bg-black/30 border border-white/10 text-white">
                <SelectValue placeholder="-- Brak (nie wyświetlaj) --" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10">
                <SelectItem value="__none__" className="text-gray-400">-- Brak (nie wyświetlaj) --</SelectItem>
                {documents.map((doc) => {
                  const docSlug = typeof doc.slug === 'object' ? doc.slug?.pl : doc.slug;
                  return (
                    <SelectItem 
                      key={docSlug || doc.document_id} 
                      value={docSlug || ''}
                      className="text-white hover:bg-white/10"
                    >
                      {doc.title?.pl || doc.title || docSlug || 'Bez tytułu'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {settings.termsOfServiceSlug && (
              <a 
                href={`/pl/dokument/${settings.termsOfServiceSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Podgląd dokumentu
              </a>
            )}
            <p className="text-sm text-gray-500">
              Jeśli wybierzesz regulamin, będzie wyświetlany obok polityki prywatności w stopce popupu.
            </p>
          </div>

          {/* Info about documents */}
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 font-medium text-sm flex items-center gap-2">
              <Info className="w-4 h-4" />
              Dokumenty możesz edytować w zakładce Dokumenty
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Treść dokumentów jest wielojęzyczna i automatycznie wyświetlana w języku użytkownika.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Consent Categories Info */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Kategorie zgód (Consent Mode v2)</CardTitle>
          <CardDescription>
            Poniższe kategorie są automatycznie obsługiwane przez popup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 font-medium">Niezbędne</p>
              <p className="text-xs text-gray-400 mt-1">security_storage - zawsze włączone</p>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-400 font-medium">Analityczne</p>
              <p className="text-xs text-gray-400 mt-1">analytics_storage - Google Analytics</p>
            </div>
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-purple-400 font-medium">Marketingowe</p>
              <p className="text-xs text-gray-400 mt-1">ad_storage, ad_user_data, ad_personalization</p>
            </div>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 font-medium">Funkcjonalne</p>
              <p className="text-xs text-gray-400 mt-1">functionality_storage, personalization_storage</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Link to translations */}
      <Card className="bg-primary/10 border-primary/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary font-medium">Teksty popupu cookie</p>
              <p className="text-sm text-gray-400 mt-1">
                Edytuj wszystkie teksty wyświetlane w popupie (tytuł, opisy kategorii, przyciski)
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/admin/translations'}
              className="border-primary/50 text-primary hover:bg-primary/20"
            >
              Tłumaczenia → Cookie Consent
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCookieConsent;
